import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, BookOpen, Award, User, Edit, CreditCard, Eye, Download, ShoppingBag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import copy from '../copy';

type TabType = 'account' | 'billing' | 'content';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('account');
  const [isEditing, setIsEditing] = useState(false);

  const goToCampus = () => {
    window.open(copy.auth.login.moodleUrl, '_blank');
  };

  if (!user) {
    navigate('/');
    return null;
  }

  // Mock data para historial de compras (vacío por ahora)
  const purchases: any[] = [];
  const enrolledCourses: any[] = [];

  const renderAccountTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{copy.dashboard.account.title}</h2>
        <p className="text-gray-600 text-sm">{copy.dashboard.account.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {copy.dashboard.account.name} *
          </label>
          <div className="relative">
            <input
              type="text"
              value={user.firstName}
              disabled={!isEditing}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-purple-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            {isEditing && (
              <Edit className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {copy.dashboard.account.lastName} *
          </label>
          <div className="relative">
            <input
              type="text"
              value={user.lastName}
              disabled={!isEditing}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-purple-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            {isEditing && (
              <Edit className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {copy.dashboard.account.email} *
        </label>
        <div className="relative">
          <input
            type="email"
            value={user.email}
            disabled={!isEditing}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-purple-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          {isEditing && (
            <Edit className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{copy.dashboard.account.changePassword}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {copy.dashboard.account.newPassword}
            </label>
            <input
              type="password"
              placeholder="••••••••"
              disabled={!isEditing}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-purple-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {copy.dashboard.account.confirmPassword}
            </label>
            <input
              type="password"
              placeholder="••••••••"
              disabled={!isEditing}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-purple-50 text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors font-medium"
        >
          {isEditing ? copy.dashboard.account.save : 'Editar'}
        </button>
      </div>
    </div>
  );

  const renderBillingTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{copy.dashboard.billing.title}</h2>
        <p className="text-gray-600 text-sm">{copy.dashboard.billing.subtitle}</p>
      </div>

      {purchases.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{copy.dashboard.billing.noPurchases}</h3>
          <p className="text-gray-600 mb-6">{copy.dashboard.billing.noPurchasesDesc}</p>
          <button
            onClick={() => navigate('/cursos')}
            className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors font-medium"
          >
            {copy.dashboard.billing.exploreCourses}
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.dashboard.billing.orderNumber}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.dashboard.billing.date}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.dashboard.billing.total}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {copy.dashboard.billing.status}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchases.map((purchase) => (
                <tr key={purchase.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    #{purchase.orderNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {purchase.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {purchase.total}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {purchase.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button className="text-orange-600 hover:text-orange-900 flex items-center space-x-1">
                      <Download className="w-4 h-4" />
                      <span>{copy.dashboard.billing.download}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderContentTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{copy.dashboard.content.title}</h2>
        <p className="text-gray-600 text-sm">{copy.dashboard.content.subtitle}</p>
      </div>

      {enrolledCourses.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{copy.dashboard.content.noCourses}</h3>
          <p className="text-gray-600 mb-6">{copy.dashboard.content.noCoursesDesc}</p>
          <button
            onClick={() => navigate('/cursos')}
            className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors font-medium"
          >
            {copy.dashboard.billing.exploreCourses}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Award className="w-5 h-5 text-green-600" />
              <p className="text-green-800 font-medium">
                ¡Tienes cursos activos!
              </p>
            </div>
            <p className="text-green-700 text-sm mt-1">
              {copy.dashboard.content.campusDesc}
            </p>
          </div>

          <button
            onClick={goToCampus}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center space-x-2"
          >
            <ExternalLink className="w-5 h-5" />
            <span>{copy.dashboard.content.goToCampus}</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {copy.dashboard.welcome}, {user.firstName}!
          </h1>
          <p className="text-gray-600 mt-1">{copy.dashboard.subtitle}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-80">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('account')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === 'account' 
                      ? 'bg-orange-500 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">{copy.dashboard.tabs.account}</span>
                </button>

                <button
                  onClick={() => setActiveTab('billing')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === 'billing' 
                      ? 'bg-orange-500 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <CreditCard className="w-5 h-5" />
                  <span className="font-medium">{copy.dashboard.tabs.billing}</span>
                </button>

                <button
                  onClick={() => setActiveTab('content')}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === 'content' 
                      ? 'bg-orange-500 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Eye className="w-5 h-5" />
                  <span className="font-medium">{copy.dashboard.tabs.content}</span>
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm p-6">
              {activeTab === 'account' && renderAccountTab()}
              {activeTab === 'billing' && renderBillingTab()}
              {activeTab === 'content' && renderContentTab()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 